/** GET /api/app/school-bands — paged item (ABP) */
export interface SchoolBandDto {
  id: string;
  creationTime?: string;
  creatorId?: string | null;
  lastModificationTime?: string | null;
  lastModifierId?: string | null;
  isDeleted?: boolean;
  deleterId?: string | null;
  deletionTime?: string | null;
  name?: string | null;
  fullName?: string | null;
  minValue: number;
  maxValue: number;
  description?: string | null;
  minSchoolsPerBand: number;
  year: number;
  /** Schools count in this band (read-only from server). */
  numberOfSchools?: number;
  /** School id → weight — when present, use for listing schools in this band (e.g. after calc). */
  schoolIds?: Record<string, number>;
  /** Full school rows for this band when returned by GET/POST (e.g. step-2 list). */
  schools?: SchoolByIdsDto[];
  /** Same value on every item in the list — schools not covered by any band. */
  totalSchoolOutOfBands?: number;
}

export interface SchoolBandsPagedResult {
  items: SchoolBandDto[];
  totalCount: number;
}

/** POST /api/app/school-bands/school-by-ids — minimal fields for list UI */
export interface SchoolByIdsDto {
  id: string;
  schoolName?: string | null;
  /** School / educational center code (API: EducationalCenterNumber). */
  educationalCenterNumber?: string | null;
  code?: string | null;
  totalStudentCount?: number;
}

/** Local edit state for step 2 (name / min / max / description editable; schools count read-only). */
export interface SizeBandEditableRow {
  key: string;
  name: string;
  minValue: number;
  maxValue: number;
  description: string;
  numberOfSchools: number;
  /** Populated when the server returns membership (e.g. calc-band-school-count). */
  schoolIds?: Record<string, number>;
  /** Populated when GET/POST returns embedded `schools` on each band. */
  schools?: SchoolByIdsDto[];
}

export function mapSchoolBandsToEditableRows(items: SchoolBandDto[]): SizeBandEditableRow[] {
  return items.map((item, i) => ({
    key: item.id && item.id !== '00000000-0000-0000-0000-000000000000' ? item.id : `band-${i}`,
    name: item.name ?? '',
    minValue: item.minValue,
    maxValue: item.maxValue,
    description: item.description ?? '',
    numberOfSchools: item.numberOfSchools ?? 0,
    schoolIds: item.schoolIds,
    schools: item.schools?.length ? item.schools : undefined,
  }));
}

/** GUID for new bands before the server assigns a persisted id. */
export const CALC_BAND_NEW_ID = '00000000-0000-0000-0000-000000000000';

/** POST /api/app/school-bands/calc-band-school-count — request body item */
export interface CalcBandSchoolCountItem {
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
  description: string;
  minSchoolsPerBand: number;
  year: number;
  /**
   * Response-only on many backends; sending `0` on POST can fail model validation.
   * Omit when calling `calc-band-school-count`.
   */
  numberOfSchools?: number;
}

/** POST body for calc-band-school-count / populate-band-membership (omit response-only fields). */
export function toCalcBandSchoolCountPostBody(
  items: CalcBandSchoolCountItem[]
): Array<
  Pick<
    CalcBandSchoolCountItem,
    'id' | 'name' | 'minValue' | 'maxValue' | 'description' | 'minSchoolsPerBand' | 'year'
  >
> {
  return items.map(
    ({ id, name, minValue, maxValue, description, minSchoolsPerBand, year }) => ({
      id,
      name,
      minValue,
      maxValue,
      description,
      minSchoolsPerBand,
      year,
    })
  );
}

/** POST /api/app/school-bands/calc-band-school-count — response body */
export interface CalcBandSchoolCountResponse {
  bands: SchoolBandDto[];
  totalSchoolOutOfBands: number;
  totalSchoolOutOfBandsIds: string[];
}

/** POST /api/app/school-bands/school-by-ids — request body */
export interface SchoolByIdsPostBody {
  year: number;
  minSchoolsPerBand: number;
  schoolIds: string[];
  /** Filter schools by name (server-side). */
  name?: string;
  skipCount?: number;
  maxResultCount?: number;
  /** e.g. `TotalStudentCount` — must match server. */
  sorting?: string;
  /** `asc` | `desc` — used with `sorting` for student count order. */
  dir?: string;
}

/** POST /api/app/school-bands/school-by-ids — paged response (ABP-style). */
export interface SchoolByIdsPagedResult {
  items: SchoolByIdsDto[];
  totalCount: number;
}
